import React, { useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { Tooltip } from '@mui/material';
import { useTranslations } from 'next-intl';

interface HeatMapData {
  date: string;
  count: number;
}

interface ProcessedDay {
  date: Date;
  count: number;
  dateStr: string;
}

interface D3HeatMapProps {
  data: HeatMapData[];
  width?: number;
  height?: number;
  cellSize?: number;
  cellGap?: number;
  panelColors: Record<number, string>;
  baseColor: string;
  onCellHover?: (data: HeatMapData | null, event?: MouseEvent) => void;
  onCellClick?: (data: HeatMapData, event?: MouseEvent) => void;
  className?: string;
  tooltipContent?: (data: ProcessedDay) => React.ReactNode;
}

export const D3HeatMap: React.FC<D3HeatMapProps> = ({
  data,
  width = 800,
  cellSize = 12,
  cellGap = 2,
  panelColors,
  baseColor,
  onCellHover,
  onCellClick,
  className = '',
  tooltipContent
}) => {
  const t = useTranslations('D3HeatMap');
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    data: ProcessedDay;
    x: number;
    y: number;
  } | null>(null);

  // 计算日期范围和数据映射
  const processedData = useMemo(() => {
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    oneYearAgo.setDate(oneYearAgo.getDate() + 1); // 从一年前的明天开始

    // 创建数据映射
    const dataMap = new Map<string, number>();
    data.forEach(d => {
      const date = new Date(d.date);
      const dateStr = d3.timeFormat('%Y-%m-%d')(date);
      dataMap.set(dateStr, d.count);
    });

    // 生成完整的日期数组
    const days: ProcessedDay[] = [];
    const currentDate = new Date(oneYearAgo);

    while (currentDate <= today) {
      const dateStr = d3.timeFormat('%Y-%m-%d')(currentDate);
      days.push({
        date: new Date(currentDate),
        count: dataMap.get(dateStr) || 0,
        dateStr
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, [data]);

  // 计算颜色强度
  const getColor = useMemo(() => {
    return (count: number) => {
      if (count === 0) return baseColor;

      // 按照新的分级逻辑
      // 0: base颜色
      // 1-4: level1
      // 5-9: level2
      // 10-14: level3
      // 15-19: level4
      // 20+: level5
      let level = 1; // 默认是level1
      if (count >= 5) level = 2;
      if (count >= 10) level = 3;
      if (count >= 15) level = 4;
      if (count >= 20) level = 5;

      return panelColors[level] || panelColors[5] || baseColor;
    };
  }, [panelColors, baseColor]);  // 计算网格布局
  const gridData = useMemo(() => {
    const weeks: ProcessedDay[][] = [];
    let currentWeek: ProcessedDay[] = [];

    processedData.forEach((day, index) => {
      const dayOfWeek = day.date.getDay(); // 0 = Sunday, 6 = Saturday

      if (index === 0) {
        // 第一天，创建空白填充
        for (let i = 0; i < dayOfWeek; i++) {
          currentWeek.push({
            date: new Date(0),
            count: -1, // 用-1标记空白格子
            dateStr: ''
          });
        }
      }

      currentWeek.push(day);

      if (dayOfWeek === 6 || index === processedData.length - 1) {
        // 周六或最后一天，完成当前周
        if (index === processedData.length - 1 && dayOfWeek !== 6) {
          // 最后一周填充到周六
          for (let i = dayOfWeek + 1; i <= 6; i++) {
            currentWeek.push({
              date: new Date(0),
              count: -1,
              dateStr: ''
            });
          }
        }
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    return weeks;
  }, [processedData]);

  // 计算SVG尺寸
  const svgWidth = width;
  const svgHeight = 7 * (cellSize + cellGap) + 40; // 7行 + 月份标签空间

  useEffect(() => {
    if (!svgRef.current || !processedData.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // 创建主要的g元素
    const g = svg.append('g')
      .attr('transform', 'translate(40, 20)');

    // 绘制月份标签
    const months = d3.timeMonths(
      d3.timeMonth.floor(processedData[0].date),
      d3.timeMonth.ceil(processedData[processedData.length - 1].date)
    );

    // 月份国际化映射
    const getMonthName = (date: Date) => {
      const monthIndex = date.getMonth();
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
        'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      return t(`months.${monthKeys[monthIndex]}`);
    };

    g.append('g')
      .selectAll('.month-label')
      .data(months)
      .enter()
      .append('text')
      .attr('class', 'month-label')
      .attr('x', (d: Date) => {
        // 计算月份标签的x位置
        const weekIndex = Math.floor(d3.timeWeek.count(d3.timeYear(d), d));
        return weekIndex * (cellSize + cellGap);
      })
      .attr('y', -5)
      .attr('font-size', '10px')
      .attr('fill', 'var(--color-muted-foreground)')
      .text((d: Date) => getMonthName(d));

    // 绘制星期标签
    const weekDayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const getWeekDayName = (index: number) => {
      return t(`weekdays.${weekDayKeys[index]}`);
    };

    g.append('g')
      .selectAll('.week-label')
      .data(weekDayKeys)
      .enter()
      .append('text')
      .attr('class', 'week-label')
      .attr('x', -25)
      .attr('y', (_d: string, i: number) => i * (cellSize + cellGap) + cellSize / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', 'var(--color-muted-foreground)')
      .text((_d: string, i: number) => i % 2 === 1 ? getWeekDayName(i) : ''); // 只显示奇数行的标签

    // 绘制热力图格子
    const weeks = g.append('g')
      .selectAll('.week')
      .data(gridData)
      .enter()
      .append('g')
      .attr('class', 'week')
      .attr('transform', (_d: ProcessedDay[], i: number) => `translate(${i * (cellSize + cellGap)}, 0)`);

    weeks.selectAll('.cell')
      .data((d: ProcessedDay[]) => d)
      .enter()
      .append('rect')
      .attr('class', 'cell')
      .attr('data-date', (d: ProcessedDay) => d.dateStr)
      .attr('data-count', (d: ProcessedDay) => d.count)
      .attr('x', 0)
      .attr('y', (_d: ProcessedDay, i: number) => i * (cellSize + cellGap))
      .attr('width', cellSize)
      .attr('height', cellSize)
      .attr('rx', cellSize * 0.5)
      .attr('ry', cellSize * 0.5)
      .attr('fill', (d: ProcessedDay) => d.count === -1 ? 'transparent' : getColor(d.count))
      .attr('stroke', 'var(--color-border)')
      .attr('stroke-width', 0.5)
      .style('cursor', (d: ProcessedDay) => d.count === -1 ? 'default' : 'pointer')
      .on('mouseenter', function (this: SVGRectElement, event: MouseEvent, d: ProcessedDay) {
        if (d.count === -1) return;

        d3.select(this)
          .attr('stroke', 'var(--color-primary)')
          .attr('stroke-width', 1.5);

        if (onCellHover) {
          onCellHover({ date: d.dateStr, count: d.count }, event);
        }

        // 获取当前 cell 相对于 SVG 的位置
        const svgRect = svgRef.current!.getBoundingClientRect();
        const cellRect = (this as SVGRectElement).getBoundingClientRect();

        setTooltipData({
          data: d,
          x: cellRect.left - svgRect.left + cellRect.width / 2 - 6,
          y: cellRect.top - svgRect.top + 6
        });
      })
      .on('mouseleave', function (this: SVGRectElement, event: MouseEvent, d: ProcessedDay) {
        if (d.count === -1) return;

        d3.select(this)
          .attr('stroke', 'var(--color-border)')
          .attr('stroke-width', 0.5);

        if (onCellHover) {
          onCellHover(null);
        }
        setTooltipData(null);
      })
      .on('click', function (this: SVGRectElement, event: MouseEvent, d: ProcessedDay) {
        if (d.count === -1) return;

        if (onCellClick) {
          onCellClick({ date: d.dateStr, count: d.count }, event);
        }
      });

  }, [processedData, gridData, cellSize, cellGap, getColor, onCellHover, onCellClick, setTooltipData, t]);

  return (
    <div className={`relative ${className}`}>
      <svg
        ref={svgRef}
        width={svgWidth}
        height={svgHeight}
        style={{ display: 'block' }}
      />

      {tooltipData && (
        <Tooltip
          open={true}
          title={
            tooltipContent ? tooltipContent(tooltipData.data) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 500 }}>
                  {tooltipData.data.count} pomodoros
                </div>
                <div style={{ fontSize: '12px', opacity: 0.75 }}>
                  {d3.timeFormat('%Y-%m-%d')(tooltipData.data.date)}
                </div>
              </div>
            )
          }
          placement="top"
          arrow
          slotProps={{
            popper: {
              sx: {
                zIndex: 99999
              }
            }
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: tooltipData.x,
              top: tooltipData.y - 5,
              width: cellSize,
              height: cellSize,
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default D3HeatMap;
